import { GlobalStyle } from 'core/style/global'
import { TypeTheme, TypeConfig } from 'core/style/theme'
import React, { Suspense, useCallback, useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { getUA, setConfig } from 'core/singleton'
import { BrowserRouter, HashRouter, Route, StaticRouter, Switch } from 'react-router-dom'
import PageLayout from 'container/PageLayout'
import { requestBridge } from 'core/util/default'
import useMediaQuery from 'core/hooks/useMediaQuery'
import { lazyWithPreload } from 'core/util/preload'
import { BRIDGE } from 'core/constants/bridge'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { GAHelper } from 'core/ga'
import PartnersRouter from 'routes/PartnersRouter'
import useAnalysis from 'core/hooks/useAnalysis'
import useInitializeSession from 'core/hooks/useInitializeSession'
import useSetThemeEventListener from 'core/hooks/useSetThemeEventListener'

const PageCuration = lazyWithPreload(() => import('./container/Page/PageCuration'))
const PageMain = lazyWithPreload(() => import('./container/Page/PageMain'))
const PageSchedule = lazyWithPreload(() => import('./container/Page/PageSchedules'))
const PageSearch = lazyWithPreload(() => import('./container/Page/PageSearch'))
const PageSearchResult = lazyWithPreload(() => import('./container/Page/PageSearchResult'))

export interface AppProps {
  partnerId?: string
  accessToken?: string
  broadcast?: boolean
  schedule?: boolean
  scheduleType?: 'image' | 'feed' | 'list'
  curation?: boolean
  curationDetail?: boolean
  autoPlay?: boolean
  searchBar?: boolean
  sampleTitle?: boolean
  bridge?: boolean
  router?: boolean
  broadcastOpenLink?: string
  theme: TypeTheme
  themeB: TypeTheme
  config: TypeConfig
  broadcastTable?: boolean
  contentBanner?: boolean
  textBanner?: boolean
  curationId?: string
  topBanner?: boolean
  sauceClip?: boolean
}

const App: React.FC<AppProps> = (props) => {
  const { theme, themeB, config, partnerId, router } = props
  const { hashRouter } = config

  const [rootProps, setRootProps] = useRecoilState(STORE_ROOT_PROPS)
  const setIsOpenLink = useSetRecoilState(IS_OPEN_LINK)

  const isDeskTop = useMediaQuery('DESKTOP')

  const { isSDK } = getUA()

  const getIsOpenLink = useCallback(() => {
    if (!rootProps?.broadcastOpenLink) return false
    const list = rootProps?.broadcastOpenLink.split(',')

    if (getUA().isWebview) return list.includes('webview')
    if (isDeskTop) return list.includes('pc')
    if (!isDeskTop) return list.includes('mobile')
    return false
  }, [isDeskTop, rootProps?.broadcastOpenLink])

  useAnalysis({ config, partnerId })

  const { isSessionInitialized } = useInitializeSession({ props })

  /**
   * 루트Prop 설정 useEffect
   */
  useEffect(() => {
    if (!isSessionInitialized) return

    setRootProps(props)
  }, [isSessionInitialized, props, setRootProps])

  useEffect(() => {
    let copiedConfig = config

    if (copiedConfig.sauceclip && copiedConfig.sauceclip.isSauceClip !== false) {
      copiedConfig = {
        ...copiedConfig,
        sauceclip: {
          ...copiedConfig.sauceclip,
          isSauceClip: true,
        },
      }
    }

    setConfig(copiedConfig)
  }, [config])

  useEffect(() => {
    if (props.bridge || window[BRIDGE.ENTER] || isSDK) {
      requestBridge(BRIDGE.ENTER, {})
    }
    new GAHelper().init(props.partnerId)
  }, [props, isSDK])

  /**
   * for test ios sdk
   */
  useEffect(() => {
    const userAgent = navigator.userAgent
    const { isWebview } = getUA()
    const isSDKIOS = /sauce-sdk-ios/gi.test(userAgent)
    if (isWebview && window.webkit) {
      window.webkit.messageHandlers.viewLoadMsg?.postMessage('Hello from JavaScript @ App.tsx')
    }
    if (isSDKIOS) {
      window.webkit.messageHandlers.viewLoadMsg?.postMessage('sdk-ios detected!')
    }
  }, [])

  useEffect(() => {
    setIsOpenLink((oldState) => getIsOpenLink())
  }, [setIsOpenLink, getIsOpenLink])

  useEffect(() => {
    const PreloadComponentList = [PageCuration, PageSchedule, PageSearch, PageSearchResult]

    PreloadComponentList.map((Component) => Component.preload())
  }, [])

  const useTheme = useSetThemeEventListener({ theme, themeB })

  if (!rootProps) {
    return <></>
  }

  let CustomRouter = StaticRouter
  if (router) {
    CustomRouter = BrowserRouter
  }
  if (hashRouter) {
    CustomRouter = HashRouter
  }

  return (
    <ThemeProvider theme={useTheme}>
      <GlobalStyle />
      <div className="sauceflex_collection">
        <CustomRouter {...(router && !hashRouter && { basename: window.location.pathname })}>
          <PageLayout>
            <Switch>
              <Suspense fallback="">
                <Route path="/" exact={true}>
                  <PartnersRouter path="/">
                    <PageMain />
                  </PartnersRouter>
                </Route>
                <Route path="/search" exact={true}>
                  <PageSearch />
                </Route>
                <Route
                  path="/search/:keyword"
                  render={(props) => <PageSearchResult keyword={props.match.params.keyword} />}
                  exact={true}
                />
                <Route
                  path="/curation/:id/:title"
                  exact={true}
                  render={(props) => (
                    <PartnersRouter path="/curation" props={props}>
                      <PageCuration id={props.match.params.id} title={props.match.params.title} />
                    </PartnersRouter>
                  )}
                />
                <Route path="/schedule" exact={true}>
                  <PartnersRouter path="/schedule">
                    <PageSchedule />
                  </PartnersRouter>
                </Route>
              </Suspense>
            </Switch>
          </PageLayout>
        </CustomRouter>
      </div>
    </ThemeProvider>
  )
}

export default App
